import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { api, getLastLaunchParams } from '@/services/api';
import { useSessionStore } from '@/stores';

/**
 * EdgePlay landing page.
 *
 * EdgePlay sessions are launched exclusively from EdgeHub via the JWT/URL-parameter
 * integration (Phase 15). There is no scenario picker — this page only ever does one
 * of three things:
 *
 *   1. Auto-launch: EdgeHub redirected here with ?scenario=... and a JWT in the
 *      session store. Create a session and navigate to /simulation/:id.
 *   2. Auto-launch error: show the failure and a Return to EdgeHub button.
 *   3. Bare landing (someone navigated to / directly): tell them to launch from
 *      EdgeHub and provide the link.
 *
 * The previous picker UI was removed in EP-18c (ROC-1110) — see PHASE-18 doc for
 * the scope correction note.
 */

const EDGEHUB_URL = import.meta.env.VITE_EDGEHUB_URL || 'http://localhost:3001';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading, isEdgeHubSession, edgeHubUserId, scenarioFromUrl, setScenarioFromUrl } = useSessionStore();
  const [autoLaunching, setAutoLaunching] = useState(false);
  const [autoLaunchError, setAutoLaunchError] = useState<string | null>(null);

  // Auto-launch when coming from EdgeHub with a scenario id on the URL.
  // We check BOTH the Zustand store (for persist-hydrated state on refresh)
  // AND the module-level launch params (which survive Zustand persist race
  // conditions in new tabs). Either source is sufficient.
  const launchParams = getLastLaunchParams();
  const hasScenario = scenarioFromUrl || launchParams?.scenario;
  const hasContext = isEdgeHubSession || !!launchParams;

  console.log('[Dashboard] hasContext=', hasContext, 'hasScenario=', hasScenario,
    'launchParams=', launchParams, 'isEdgeHubSession=', isEdgeHubSession,
    'scenarioFromUrl=', scenarioFromUrl, 'autoLaunching=', autoLaunching);

  useEffect(() => {
    console.log('[Dashboard useEffect] hasContext=', hasContext, 'hasScenario=', hasScenario, 'autoLaunching=', autoLaunching);
    if (hasContext && hasScenario && !autoLaunching) {
      setAutoLaunching(true);
      const scenarioId = scenarioFromUrl || launchParams?.scenario || '';
      setScenarioFromUrl(null); // prevent re-trigger
      (async () => {
        try {
          setLoading(true);
          const session = await api.createSession({
            userId: edgeHubUserId || '',
            scenarioId,
            candidateResultVisibility: launchParams?.visibility || '',
          });
          // The backend returns an existing session if one is already in progress
          // or completed (resume / results-redirect). Route accordingly.
          if (session.status === 'Completed') {
            navigate(`/results/${session.id}`);
          } else {
            navigate(`/simulation/${session.id}`);
          }
        } catch (error: unknown) {
          console.error('Error auto-creating session:', error);
          const err = error as { response?: { data?: unknown }; message?: string };
          const message = err?.response?.data ?? err?.message ?? 'Failed to create simulation session';
          setAutoLaunchError(typeof message === 'string' ? message : 'Failed to create simulation session');
          setAutoLaunching(false);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [hasContext, hasScenario]);

  // Loading state while auto-launching into a scenario
  if (autoLaunching || scenarioFromUrl) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
          Launching simulation...
        </Typography>
      </Container>
    );
  }

  // Auto-launch failed
  if (autoLaunchError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Alert severity="error" sx={{ mt: 3, mb: 3 }}>
          {autoLaunchError}
        </Alert>
        <Button
          variant="contained"
          onClick={() => { window.location.href = `${EDGEHUB_URL}/on-demand`; }}
        >
          Return to EdgeHub
        </Button>
      </Container>
    );
  }

  // Default landing state — sessions are launched from EdgeHub only.
  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h3" component="h1" gutterBottom color="primary">
        EdgePlay
      </Typography>
      <Typography variant="h6" component="h2" color="text.secondary" mb={4}>
        Modular Business Simulation Platform
      </Typography>
      <Alert severity="info" sx={{ mt: 3, mb: 3, textAlign: 'left' }}>
        EdgePlay simulations are launched from EdgeHub. Visit the On Demand catalog
        to choose a scenario and start a session.
      </Alert>
      <Button
        variant="contained"
        size="large"
        onClick={() => { window.location.href = `${EDGEHUB_URL}/on-demand`; }}
      >
        Go to EdgeHub
      </Button>
    </Container>
  );
};

export default Dashboard;
